import { useEffect, useSyncExternalStore } from 'react';

/**
 * F-0739 (PJ-8, I-2232): the Public work area a signed-in non-member is reading, for the shell's
 * work-area selector, which otherwise says "No work area selected" beside a page that is plainly in
 * one. Set by `PublicWorkAreaGate` while it draws the view, cleared when it stops; and by a namespace's
 * page of Public work areas (F-0788 PJ-10c), which names the namespace (`id` `namespace:<path>`).
 * Memory only: nothing is stored in the browser (AC-PV21).
 */
export interface IViewedPublicWorkArea {
  id: string;
  name: string;
}

let viewed: IViewedPublicWorkArea | null = null;
const listeners = new Set<() => void>();

export function setViewedPublicWorkArea(next: IViewedPublicWorkArea | null): void {
  if (viewed?.id === next?.id && viewed?.name === next?.name) return;
  viewed = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useViewedPublicWorkArea(): IViewedPublicWorkArea | null {
  return useSyncExternalStore(subscribe, () => viewed);
}

/**
 * F-0788 (PJ-10c, PJ-10e): while a namespace's page is drawn, the selector, the bar and the sidebar
 * name that namespace (`namespace:<path>`), a member's page and a stranger's alike. Nothing while
 * `name` is null (the stranger's list has not answered).
 */
export function useNamespaceNamedInSelector(path: string, name: string | null): void {
  useEffect(() => {
    if (!name) return undefined;
    setViewedPublicWorkArea({ id: `namespace:${path}`, name });
    return () => setViewedPublicWorkArea(null);
  }, [path, name]);
}
